// js/components/customerNav.js
// Persistent bottom navigation for customer pages.
// Rendered once; setActive(path) updates the active item without re-rendering.
// Returns { setActive, setUnreadCount } so the router and inbox can update state.

import { refreshUI } from "../lib/ui.js";
import { t } from "../lib/i18n.js";
import { BRAND_NAME } from "../lib/config.js";

// Bottom nav tabs (left of FAB)
const LEFT_HREFS = [
  { href: "/dashboard", i18nKey: "customerNav.home", icon: "home" },
  { href: "/shop-info", i18nKey: "customerNav.shopInfo", icon: "store" },
];

// Bottom nav tabs (right of FAB)
const RIGHT_HREFS = [
  { href: "/history", i18nKey: "customerNav.history", icon: "history" },
];

// Sidebar-only links — hidden on mobile (drawer handles them), visible on desktop sidebar
const SIDEBAR_ONLY_HREFS = [
  { href: "/account", i18nKey: "customerNav.account", icon: "circle-user" },
  { href: "/messages", i18nKey: "customerNav.messages", icon: "bell" },
];

// Links shown inside the "More" slide-in drawer
const DRAWER_LINKS = [
  { href: "/account", i18nKey: "customerNav.account", icon: "circle-user" },
  { href: "/messages", i18nKey: "customerNav.messages", icon: "bell" },
];

// Routes where the "More" button shows as active
const MORE_PATHS = ["/account", "/messages"];

/**
 * Render the customer bottom nav into the given container element.
 * @param {HTMLElement} container  The <nav id="app-nav"> element.
 * @param {{ openQrModal: () => void, navigate: Function }} opts
 * @returns {{ setActive: (path: string) => void, setUnreadCount: (n: number) => void }}
 */
export function renderCustomerNav(container, { openQrModal, navigate }) {
  container.className = "bottom-nav bottom-nav--customer";

  container.innerHTML = `
    <div class="bottom-nav__inner">
      <div class="nav-brand">
        <span class="nav-brand__powered">powered by</span>
        <span class="nav-brand__name">${BRAND_NAME}</span>
      </div>

      ${LEFT_HREFS.map((link) => navItem(link)).join("")}

      <div class="bottom-nav__fab-slot">
        <button class="bottom-nav__fab" id="customer-qr-fab" aria-label="QR Code">
          <i data-lucide="qr-code" aria-hidden="true"></i>
        </button>
      </div>

      ${RIGHT_HREFS.map((link) => navItem(link)).join("")}

      ${SIDEBAR_ONLY_HREFS.map((link) => navItem({ ...link, sidebarOnly: true })).join("")}

      <button
        class="bottom-nav__link bottom-nav__link--mobile-only"
        id="customer-more-btn"
        type="button"
        aria-label="${t("customerNav.more")}"
        aria-expanded="false"
        aria-haspopup="true"
      >
        <span class="bottom-nav__more-icon-wrap">
          <i data-lucide="ellipsis-vertical" aria-hidden="true"></i>
          <span class="bottom-nav__unread-badge" id="customer-unread-badge" hidden aria-hidden="true"></span>
        </span>
        <span class="bottom-nav__label" data-i18n="customerNav.more">${t("customerNav.more")}</span>
      </button>
    </div>
  `;

  // ── More drawer — appended to document.body to escape backdrop-filter
  //    stacking context that would otherwise clip position:fixed children ──────
  document.querySelector("#customer-more-drawer-backdrop")?.remove();
  document.querySelector("#customer-more-drawer")?.remove();

  const backdropEl = document.createElement("div");
  backdropEl.id = "customer-more-drawer-backdrop";
  backdropEl.className = "more-drawer__backdrop";
  backdropEl.setAttribute("aria-hidden", "true");

  const drawerEl = document.createElement("div");
  drawerEl.id = "customer-more-drawer";
  drawerEl.className = "more-drawer";
  drawerEl.setAttribute("role", "dialog");
  drawerEl.setAttribute("aria-modal", "true");
  drawerEl.setAttribute("aria-label", t("customerNav.more"));
  drawerEl.setAttribute("aria-hidden", "true");
  drawerEl.innerHTML = `
    <div class="more-drawer__header">
      <span class="more-drawer__title" data-i18n="customerNav.more">${t("customerNav.more")}</span>
      <button class="more-drawer__close" id="customer-more-drawer-close" aria-label="${t("customerNav.closeMore")}">
        <i data-lucide="x" aria-hidden="true"></i>
      </button>
    </div>
    <nav class="more-drawer__nav">
      ${DRAWER_LINKS.map((link) => drawerItem(link)).join("")}
    </nav>
  `;

  document.body.appendChild(backdropEl);
  document.body.appendChild(drawerEl);

  refreshUI();

  // ── Drawer state ────────────────────────────────────────────────────────────
  const moreBtn = container.querySelector("#customer-more-btn");
  const drawer = drawerEl;
  const backdrop = backdropEl;

  function openDrawer() {
    drawer.classList.add("more-drawer--open");
    drawer.setAttribute("aria-hidden", "false");
    backdrop.classList.add("more-drawer__backdrop--visible");
    backdrop.setAttribute("aria-hidden", "false");
    moreBtn?.setAttribute("aria-expanded", "true");
  }

  function closeDrawer() {
    // Move focus away before hiding so assistive tech doesn't warn about
    // aria-hidden receiving focus.
    if (drawer.contains(document.activeElement)) {
      moreBtn?.focus();
    }
    drawer.classList.remove("more-drawer--open");
    drawer.setAttribute("aria-hidden", "true");
    backdrop.classList.remove("more-drawer__backdrop--visible");
    backdrop.setAttribute("aria-hidden", "true");
    moreBtn?.setAttribute("aria-expanded", "false");
  }

  moreBtn?.addEventListener("click", () => {
    const isOpen = drawer.classList.contains("more-drawer--open");
    isOpen ? closeDrawer() : openDrawer();
  });

  backdrop.addEventListener("click", closeDrawer);

  drawer
    .querySelector("#customer-more-drawer-close")
    ?.addEventListener("click", closeDrawer);

  // Wire drawer items
  drawer.querySelectorAll(".more-drawer__item").forEach((el) => {
    el.addEventListener("click", (e) => {
      e.preventDefault();
      closeDrawer();
      navigate(el.getAttribute("href"));
    });
  });

  // Wire bottom nav links (SPA navigation)
  container.querySelectorAll(".bottom-nav__link[href]").forEach((el) => {
    el.addEventListener("click", (e) => {
      e.preventDefault();
      navigate(el.getAttribute("href"));
    });
  });

  // QR FAB — opens the floating QR modal
  container.querySelector("#customer-qr-fab")?.addEventListener("click", () => {
    openQrModal();
  });

  return {
    setUnreadCount(count) {
      // Mobile: badge on the More button
      const badge = container.querySelector("#customer-unread-badge");
      if (badge) {
        if (count > 0) {
          badge.textContent = count > 99 ? "99+" : String(count);
          badge.hidden = false;
          badge.removeAttribute("aria-hidden");
        } else {
          badge.hidden = true;
          badge.setAttribute("aria-hidden", "true");
        }
      }

      // Helper: add/remove a count badge on any element
      function syncBadge(parentEl) {
        if (!parentEl) return;
        let itemBadge = parentEl.querySelector(
          ".more-drawer__item-badge, .nav-link-badge",
        );
        if (count > 0) {
          if (!itemBadge) {
            itemBadge = document.createElement("span");
            itemBadge.className = parentEl.classList.contains(
              "more-drawer__item",
            )
              ? "more-drawer__item-badge"
              : "nav-link-badge";
            parentEl.appendChild(itemBadge);
          }
          itemBadge.textContent = count > 99 ? "99+" : String(count);
        } else {
          itemBadge?.remove();
        }
      }

      // Mobile drawer: badge on the Messages drawer item
      syncBadge(drawer.querySelector(".more-drawer__item[href='/messages']"));
      // Desktop sidebar: badge on the sidebar-only Messages link
      syncBadge(
        container.querySelector(
          ".bottom-nav__link--sidebar-only[href='/messages']",
        ),
      );
    },

    setActive(path) {
      // Regular nav links
      container.querySelectorAll(".bottom-nav__link[href]").forEach((el) => {
        const href = el.getAttribute("href");
        const active = path === href || (href !== "/" && path.startsWith(href));
        el.setAttribute("aria-current", active ? "page" : "false");
        el.classList.toggle("bottom-nav__link--active", active);
      });

      // "More" button active state
      const isMoreActive = MORE_PATHS.some(
        (p) => path === p || path.startsWith(p + "/"),
      );
      moreBtn?.classList.toggle("bottom-nav__link--active", isMoreActive);
      moreBtn?.setAttribute("aria-current", isMoreActive ? "page" : "false");

      // Drawer items active state
      drawer.querySelectorAll(".more-drawer__item").forEach((el) => {
        const href = el.getAttribute("href");
        const active = path === href || path.startsWith(href + "/");
        el.classList.toggle("more-drawer__item--active", active);
        el.setAttribute("aria-current", active ? "page" : "false");
      });

      // Close drawer on navigation
      closeDrawer();
    },
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function navItem({ href, i18nKey, icon, sidebarOnly = false }) {
  const label = t(i18nKey);
  return `
    <a href="${href}"
       class="bottom-nav__link${sidebarOnly ? " bottom-nav__link--sidebar-only" : ""}"
       aria-current="false"
       data-i18n-aria="${i18nKey}"
       aria-label="${label}">
      <i data-lucide="${icon}" aria-hidden="true"></i>
      <span class="bottom-nav__label" data-i18n="${i18nKey}">${label}</span>
    </a>
  `;
}

function drawerItem({ href, i18nKey, icon }) {
  const label = t(i18nKey);
  return `
    <a href="${href}"
       class="more-drawer__item"
       aria-current="false"
       data-i18n-aria="${i18nKey}"
       aria-label="${label}">
      <i data-lucide="${icon}" aria-hidden="true"></i>
      <span data-i18n="${i18nKey}">${label}</span>
    </a>
  `;
}
